import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Box, Alert, CircularProgress, IconButton,
  Collapse, Tooltip, Card, CardContent, Grid, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Button, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import StorageIcon from '@mui/icons-material/Storage';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { swarmApi } from '../api';

const stateColors = {
  running: 'success',
  exited: 'default',
  created: 'info',
  restarting: 'warning',
  removing: 'error',
  paused: 'warning',
  dead: 'error',
};

function Containers() {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedContainer, setExpandedContainer] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [logsDialog, setLogsDialog] = useState({ open: false, id: null, name: '', logs: '', loading: false });
  const [actionLoading, setActionLoading] = useState(null);
  const [pruneLoading, setPruneLoading] = useState(false);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();
  const isAdmin = user?.role === 'admin';

  const fetchContainers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await swarmApi.listContainers(true);
      setContainers(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch containers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContainers();
    const interval = setInterval(fetchContainers, 15000);
    return () => clearInterval(interval);
  }, [fetchContainers]);

  const toggleExpand = (id) => {
    setExpandedContainer(expandedContainer === id ? null : id);
  };

  const handleViewLogs = async (id, name) => {
    setLogsDialog({ open: true, id, name, logs: '', loading: true });
    try {
      const res = await swarmApi.getContainerLogs(id, 200);
      setLogsDialog(prev => ({ ...prev, logs: res.data.logs || 'No logs available', loading: false }));
    } catch {
      setLogsDialog(prev => ({ ...prev, logs: 'Failed to load logs', loading: false }));
    }
  };

  const handleStart = async (id) => {
    setActionLoading(id);
    try {
      await swarmApi.startContainer(id);
      await fetchContainers();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleStop = async (id) => {
    setActionLoading(id);
    try {
      await swarmApi.stopContainer(id);
      await fetchContainers();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this container? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await swarmApi.removeContainer(id);
      await fetchContainers();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handlePrune = async () => {
    const deadCount = containers.filter(c => c.state !== 'running').length;
    if (deadCount === 0) {
      alert('No stopped or dead containers to remove.');
      return;
    }
    if (!window.confirm(`Remove all ${deadCount} non-running container${deadCount !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setPruneLoading(true);
    try {
      const res = await swarmApi.pruneContainers();
      const mb = ((res.data.spaceReclaimed || 0) / (1024 * 1024)).toFixed(1);
      alert(`Pruned ${res.data.removed || 0} container${(res.data.removed || 0) !== 1 ? 's' : ''}, reclaimed ${mb} MB`);
      await fetchContainers();
    } catch {
      alert('Prune failed. Check server logs.');
    }
    setPruneLoading(false);
  };

  // Filters
  const filtered = containers
    .filter(c => filter === 'all' || c.state === filter)
    .filter(c => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (c.name || '').toLowerCase().includes(q)
        || (c.image || '').toLowerCase().includes(q)
        || (c.id || '').toLowerCase().includes(q);
    });

  // Counts
  const runningCount = containers.filter(c => c.state === 'running').length;
  const stoppedCount = containers.filter(c => c.state === 'exited').length;
  const otherCount = containers.length - runningCount - stoppedCount;

  // Extract service name from Swarm container name (format: stack_service.slot.taskid)
  const getServiceName = (name) => {
    if (!name) return null;
    const parts = name.split('.');
    if (parts.length >= 2) return parts[0]; // stack_service
    return null;
  };

  // Format created date
  const formatAge = (created) => {
    if (!created) return '—';
    const d = new Date(created * 1000); // Docker returns epoch seconds
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays}d ago`;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewInArIcon fontSize="large" color="primary" /> Containers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All Docker containers on this node
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isAdmin && (
            <Tooltip title="Remove all stopped and dead containers">
              <span>
                <Button
                  startIcon={pruneLoading ? <CircularProgress size={16} /> : <CleaningServicesIcon />}
                  onClick={handlePrune}
                  disabled={pruneLoading}
                  color="warning"
                  variant="outlined"
                  size="small"
                >
                  Prune ({containers.filter(c => c.state !== 'running').length})
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={fetchContainers} color="primary" size="large">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <StorageIcon color="primary" />
              <Typography variant="h5">{containers.length}</Typography>
              <Typography variant="caption" color="text.secondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h5">{runningCount}</Typography>
              <Typography variant="caption" color="text.secondary">Running</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CancelIcon color="error" />
              <Typography variant="h5">{stoppedCount}</Typography>
              <Typography variant="caption" color="text.secondary">Exited</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PauseCircleIcon color="warning" />
              <Typography variant="h5">{otherCount}</Typography>
              <Typography variant="caption" color="text.secondary">Other</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, image, or ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 280 }}
        />
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
          size="small"
        >
          <ToggleButton value="all">All ({containers.length})</ToggleButton>
          <ToggleButton value="running">Running ({runningCount})</ToggleButton>
          <ToggleButton value="exited">Exited ({stoppedCount})</ToggleButton>
          <ToggleButton value="created">Created</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && containers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Image</strong></TableCell>
                <TableCell><strong>State</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Ports</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => {
                const svcName = getServiceName(c.name);
                return (
                  <React.Fragment key={c.id}>
                    <TableRow
                      hover
                      onClick={() => toggleExpand(c.id)}
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedContainer === c.id ? 'unset' : undefined } }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedContainer === c.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: 300 }}>
                          {c.name || c.id?.substring(0, 12)}
                        </Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.3 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {c.id?.substring(0, 12)}
                          </Typography>
                          {svcName && (
                            <Chip label={svcName} size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontSize: '0.85rem' }}>
                          {c.image?.split('@')[0]?.split(':').length > 1
                            ? c.image?.split('@')[0]
                            : c.image?.split('@')[0] + ':latest'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={c.state}
                          size="small"
                          color={stateColors[c.state] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {c.status}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {formatAge(c.created)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {c.ports?.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {c.ports
                              .filter(p => p.publicPort)
                              .slice(0, 3)
                              .map((p, i) => (
                                <Chip
                                  key={i}
                                  label={`${p.publicPort}→${p.privatePort}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            {c.ports.filter(p => p.publicPort).length > 3 && (
                              <Chip label={`+${c.ports.filter(p => p.publicPort).length - 3}`} size="small" />
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0} justifyContent="center">
                          <Tooltip title="View Logs">
                            <IconButton size="small" onClick={() => handleViewLogs(c.id, c.name)}>
                              <ArticleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && c.state !== 'running' && (
                            <Tooltip title="Start">
                              <IconButton size="small" color="success" onClick={() => handleStart(c.id)} disabled={actionLoading === c.id}>
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && c.state === 'running' && (
                            <Tooltip title="Stop">
                              <IconButton size="small" color="warning" onClick={() => handleStop(c.id)} disabled={actionLoading === c.id}>
                                <StopIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => handleRemove(c.id)} disabled={actionLoading === c.id}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expanded detail row */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                        <Collapse in={expandedContainer === c.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
                            <Grid container spacing={3}>
                              {/* Networks */}
                              <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" gutterBottom>Networks</Typography>
                                {c.networks?.length > 0 ? (
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {c.networks.map((net) => (
                                      <Chip key={net} label={net} size="small" variant="outlined" color="primary" />
                                    ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                              </Grid>

                              {/* Ports detail */}
                              <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" gutterBottom>Ports</Typography>
                                {c.ports?.length > 0 ? (
                                  <Stack spacing={0.5}>
                                    {c.ports.map((p, i) => (
                                      <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                        {p.publicPort ? `${p.publicPort}→` : ''}{p.privatePort}/{p.type}
                                      </Typography>
                                    ))}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                              </Grid>

                              {/* Container Details */}
                              <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" gutterBottom>Details</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                  <strong>Full ID:</strong> {c.id}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Image:</strong> {c.image}
                                </Typography>
                              </Grid>

                              {/* Labels */}
                              {c.labels && Object.keys(c.labels).length > 0 && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" gutterBottom>Labels</Typography>
                                  <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      {Object.entries(c.labels).map(([k, v]) => (
                                        <Chip
                                          key={k}
                                          label={`${k}=${v}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', maxWidth: 400 }}
                                        />
                                      ))}
                                    </Stack>
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {containers.length === 0 ? 'No containers found' : 'No containers match the current filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Logs Dialog */}
      <Dialog open={logsDialog.open} onClose={() => setLogsDialog({ open: false, id: null, name: '', logs: '', loading: false })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontFamily: 'monospace' }}>
          Logs — {logsDialog.name || logsDialog.id?.substring(0, 12)}
        </DialogTitle>
        <DialogContent>
          {logsDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.900',
                color: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                maxHeight: 500,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {logsDialog.logs || 'No logs available'}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialog({ open: false, id: null, name: '', logs: '', loading: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Containers;
